import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import { useMutation } from "@apollo/client";
import { SEND_REMINDER_TO_EMPLOYEE } from "../../apis/settingsApis/assets-apis/assetsApis";
import { SECURITY_CHECKS, getCheckStatus } from "./assetTableConfig";
import { CheckStatus } from "../../utils/enums";

// What each failing check is called and why it failed, keyed by the check fields
// in SECURITY_CHECKS so the two cannot drift apart.
const CHECK_FAILURE_REASONS = {
  diskEnc: { name: "Disk Encryption", reason: "Disk encryption is disabled" },
  antivirus: { name: "Antivirus", reason: "Antivirus is not installed" },
  firewall: { name: "Firewall", reason: "Firewall is disabled" },
  autoUpdate: { name: "Auto Update", reason: "System is not up to date" },
  screenLock: { name: "Screen Lock", reason: "Screen lock is not configured" },
};

const getOwnerInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const SendFixIssuesReminderDialog = ({ open, onClose, onSuccess, selectedAssets = [] }) => {
  const theme = useTheme();

  const [sendReminder, { loading: sending }] = useMutation(SEND_REMINDER_TO_EMPLOYEE);

  // Read through getCheckStatus, which prefers the server's evaluated
  // checkStatus.checks and only falls back to the flat legacy booleans. Reading
  // those booleans directly — as this did — reported no failures at all for any
  // device whose results come from checkmate, which is all of them now.
  const getFailingChecks = (asset) =>
    SECURITY_CHECKS.map((chk) => ({ chk, status: getCheckStatus(asset, chk) }))
      .filter(
        ({ status }) => status === CheckStatus.FAIL || status === CheckStatus.PENDING
      )
      .map(({ chk, status }) => {
        const config = CHECK_FAILURE_REASONS[chk.field];
        if (!config) return null;
        return status === CheckStatus.PENDING
          ? { name: config.name, reason: "No result has been reported for this device yet" }
          : config;
      })
      .filter(Boolean);

  const handleSend = async () => {
    await Promise.all(
      selectedAssets
        .map((asset) => ({ id: asset.assetId || asset.id, checks: getFailingChecks(asset) }))
        .filter(({ id }) => Boolean(id))
        .map(({ id, checks }) =>
          sendReminder({
            variables: {
              type: "fix_agents_check_reminder",
              options: {
                assetId: id,
                failingChecks: checks,
              },
            },
          })
        )
    );
    onClose?.();
    onSuccess?.("Reminders will be sent to the device owners shortly.");
  };

  const handleClose = () => {
    onClose?.();
  };

  const ownerSummary = React.useMemo(() => {
    const map = new Map();
    selectedAssets.forEach((asset) => {
      const key = asset.ownerEmail || asset.ownerEmployeeId || asset.ownerName;
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, { name: asset.ownerName || "Unknown", email: asset.ownerEmail || "", deviceCount: 0 });
      }
      map.get(key).deviceCount += 1;
    });
    return [...map.values()];
  }, [selectedAssets]);

  const unownedCount = selectedAssets.filter(
    (a) => !a.ownerEmail && !a.ownerEmployeeId && !a.ownerName
  ).length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1rem",
          color: theme.customColors.text.strong,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        Security Vulnerabilities Reminder
        <IconButton onClick={handleClose} sx={{ color: theme.customColors.icons.dark }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: theme.customColors.background.main }}>
        <Box sx={{ p: 2.5 }}>
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              A reminder email will be sent to the owners of the{" "}
              <strong>{selectedAssets.length}</strong> selected device
              {selectedAssets.length !== 1 ? "s" : ""} asking them to resolve security issues.
            </Alert>

            {/* Summary tiles */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.customColors.border.main}`,
                  backgroundColor: theme.palette.background.paper,
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" fontWeight={700} sx={{ color: theme.customColors.text.strong }}>
                  {selectedAssets.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Devices
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.customColors.border.main}`,
                  backgroundColor: theme.palette.background.paper,
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" fontWeight={700} sx={{ color: theme.customColors.text.strong }}>
                  {ownerSummary.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recipients
                </Typography>
              </Box>
            </Box>

            {/* Owner list */}
            {ownerSummary.length > 0 && (
              <Box
                sx={{
                  border: `1px solid ${theme.customColors.border.main}`,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  overflow: "hidden",
                  maxHeight: 260,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    color: theme.customColors.text.secondary,
                    borderBottom: `1px solid ${theme.customColors.background.light}`,
                    backgroundColor: theme.customColors.background.main,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "11px",
                  }}
                >
                  Recipients
                </Typography>
                <List dense disablePadding>
                  {ownerSummary.map((owner, i) => (
                    <ListItem
                      key={i}
                      sx={{
                        px: 2,
                        py: 1,
                        borderBottom:
                          i < ownerSummary.length - 1
                            ? `1px solid ${theme.customColors.background.light}`
                            : "none",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: "11px",
                          bgcolor: theme.customColors.button.primary,
                          color: theme.customColors.button.text,
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      >
                        {getOwnerInitials(owner.name)}
                      </Avatar>
                      <ListItemText
                        primary={owner.name}
                        secondary={owner.email || "No email"}
                        primaryTypographyProps={{
                          variant: "body2",
                          fontWeight: 600,
                          color: theme.customColors.text.strong,
                        }}
                        secondaryTypographyProps={{
                          variant: "body2",
                          color: theme.customColors.text.secondary,
                        }}
                      />
                      <Chip
                        label={`${owner.deviceCount} device${owner.deviceCount !== 1 ? "s" : ""}`}
                        size="small"
                        icon={<DevicesOtherIcon sx={{ fontSize: "12px !important" }} />}
                        sx={{
                          fontSize: "0.75rem",
                          bgcolor: theme.customColors.background.light,
                          color: theme.customColors.text.strong,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {unownedCount > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>{unownedCount}</strong> device{unownedCount !== 1 ? "s" : ""}{" "}
                {unownedCount !== 1 ? "have" : "has"} no owner — no reminder will be sent for{" "}
                {unownedCount !== 1 ? "them" : "it"}.
              </Alert>
            )}
          </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${theme.customColors.border.main}`,
          backgroundColor: theme.customColors.background.main,
          px: 2.5,
          py: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            borderColor: theme.customColors.border.main,
            color: theme.customColors.text.strong,
            "&:hover": {
              borderColor: theme.customColors.border.strong,
              backgroundColor: theme.customColors.background.hover,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || ownerSummary.length === 0}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            bgcolor: theme.customColors.button.primary,
            color: theme.customColors.button.text,
            "&:hover": { bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary },
            "&:disabled": {
              backgroundColor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
            minWidth: 140,
          }}
        >
          {sending ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Send Reminder"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendFixIssuesReminderDialog;
